import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ClipboardCheck, Camera, CheckCircle, XCircle, ChevronRight, 
  Plus, Calendar, AlertTriangle, Check, X, ChevronDown, Truck,
  Trash2, Eye, Download, Printer, Undo2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";
import html2pdf from "html2pdf.js";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

// Inspection items list
const INSPECTION_ITEMS = [
  { key: "air_lines", name: "Air Lines" },
  { key: "battery", name: "Battery" },
  { key: "belts_hoses", name: "Belts & Hoses" },
  { key: "body", name: "Body" },
  { key: "brake_accessories", name: "Brake Accessories" },
  { key: "brakes_parking", name: "Brakes (Parking)" },
  { key: "brakes_service", name: "Brakes (Service)" },
  { key: "clutch", name: "Clutch" },
  { key: "coupling_devices", name: "Coupling Devices" },
  { key: "defroster_heater", name: "Defroster/Heater" },
  { key: "driveline", name: "Driveline" },
  { key: "electrical", name: "Electrical" },
  { key: "engine", name: "Engine" },
  { key: "exhaust", name: "Exhaust" },
  { key: "fifth_wheel", name: "Fifth Wheel" },
  { key: "front_axle", name: "Front Axle" },
  { key: "fuel_tanks", name: "Fuel Tanks" },
  { key: "horn", name: "Horn" },
  { key: "lights_all", name: "Lights (All)" },
  { key: "mirrors", name: "Mirrors" },
  { key: "muffler", name: "Muffler" },
  { key: "oil_level", name: "Oil Level" },
  { key: "radiator", name: "Radiator" },
  { key: "rear_end", name: "Rear End" },
  { key: "reflectors", name: "Reflectors" },
  { key: "safety_equipment", name: "Safety Equipment" },
  { key: "starter", name: "Starter" },
  { key: "steering", name: "Steering" },
  { key: "suspension", name: "Suspension" },
  { key: "tire_chains", name: "Tire Chains" },
  { key: "tires", name: "Tires" },
  { key: "transmission", name: "Transmission" },
  { key: "trip_recorder", name: "Trip Recorder" },
  { key: "wheels_rims", name: "Wheels & Rims" },
  { key: "windows", name: "Windows" },
  { key: "windshield_wipers", name: "Windshield Wipers" },
  { key: "other", name: "Other" }
];

// Step Progress Component
const StepProgress = ({ currentStep }) => {
  const steps = [
    { num: 1, label: "Vehicle" },
    { num: 2, label: "Photos" },
    { num: 3, label: "Inspection" },
    { num: 4, label: "Review" }
  ];
  
  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2">
      {steps.map((s, idx) => (
        <React.Fragment key={s.num}>
          <div className="flex flex-col items-center">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === s.num ? 'bg-blue-600 text-white' : 
                currentStep > s.num ? 'bg-green-500 text-white' : 
                'bg-slate-200 text-slate-600'
              }`}
            >
              {currentStep > s.num ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className="text-[10px] text-slate-500 mt-1 hidden sm:block">{s.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-8 sm:w-12 h-0.5 ${currentStep > s.num ? 'bg-green-500' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Photo Upload Box Component
const PhotoUploadBox = ({ label, photo, onUpload, onRemove }) => {
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    // Compress and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 1200;
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        onUpload(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  
  return (
    <div className="relative">
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {photo ? (
        <div className="relative aspect-video rounded-lg overflow-hidden border border-slate-200">
          <img src={photo} alt={label} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
          <Camera className="w-8 h-8 text-slate-400 mb-1" />
          <span className="text-xs text-slate-500">Tap to capture</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
      )}
    </div>
  );
};

// Inspection Item Row Component - Tap to mark defective or edit existing defect
const InspectionItemRow = ({ item, status, remarks, photos, onMarkDefective, onEditDefect, onClearDefect }) => {
  const isDefective = status === 'defective';
  
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => isDefective ? onEditDefect(item) : onMarkDefective(item)}
        className={`flex-1 flex items-center justify-between p-3 rounded-lg border transition-colors ${
          isDefective 
            ? 'bg-red-50 border-red-300 hover:bg-red-100' 
            : 'bg-green-50 border-green-200 hover:border-red-300 hover:bg-red-50/50'
        }`}
      >
        <div className="flex-1 text-left">
          <span className={`text-sm font-medium ${isDefective ? 'text-red-700' : 'text-slate-700'}`}>
            {item.name}
          </span>
          {isDefective && remarks && (
            <p className="text-xs text-red-600 mt-0.5 truncate max-w-[200px]">{remarks}</p>
          )}
          {isDefective && photos && photos.length > 0 && (
            <span className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
              <Camera className="w-3 h-3" /> {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isDefective ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {isDefective ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
        </div>
      </button>
      {isDefective && (
        <button
          type="button"
          onClick={() => onClearDefect(item.key)}
          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-slate-700"
          title="Clear defect"
        >
          <Undo2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Defect Details Dialog - Full viewport
const DefectDetailsDialog = ({ open, onClose, item, remarks, photos, onSave }) => {
  const [localRemarks, setLocalRemarks] = useState(remarks || '');
  const [localPhotos, setLocalPhotos] = useState(photos || []);
  
  useEffect(() => {
    setLocalRemarks(remarks || '');
    setLocalPhotos(photos || []);
  }, [remarks, photos, open]);
  
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        continue;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 800;
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          setLocalPhotos(prev => [...prev, canvas.toDataURL('image/jpeg', 0.7)]);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };
  
  const removePhoto = (index) => {
    setLocalPhotos(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSave = () => {
    onSave(localRemarks, localPhotos);
    onClose();
  };
  
  if (!item) return null;
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:m-auto">
        <DialogHeader className="flex-shrink-0 p-4 border-b bg-red-50">
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            Defect: {item.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div>
            <Label className="text-base font-semibold">Describe the defect *</Label>
            <Textarea
              value={localRemarks}
              onChange={(e) => setLocalRemarks(e.target.value)}
              placeholder="What's wrong with this item? Be specific..."
              className="min-h-[120px] mt-2 text-base"
              data-testid="input-defect-remarks"
            />
          </div>
          
          <div>
            <Label className="text-base font-semibold">Photos (Optional)</Label>
            <p className="text-sm text-slate-500 mt-1 mb-3">Add photos to document the defect</p>
            
            {localPhotos.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                {localPhotos.map((photo, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border-2 border-slate-200">
                    <img src={photo} alt={`Defect ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors">
              <Camera className="w-8 h-8 text-slate-400" />
              <span className="text-sm text-slate-600 font-medium">Tap to add photos</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
        
        <div className="flex-shrink-0 p-4 border-t bg-white flex gap-3" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button variant="outline" onClick={onClose} className="flex-1 h-12">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!localRemarks.trim()}
            className="flex-1 h-12 bg-red-600 hover:bg-red-700"
            data-testid="btn-save-defect"
          >
            Save Defect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// DVIR Report Viewer Component
const DVIRReportViewer = ({ open, onClose, dvir }) => {
  const reportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    
    setDownloading(true);
    try {
      const element = reportRef.current;
      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `DVIR_Unit${dvir.unit_number}_${new Date(dvir.created_at).toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          logging: false,
          allowTaint: true
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Failed to generate PDF", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };
  
  if (!dvir) return null;
  
  const satisfactoryItems = dvir.inspection_items?.filter(i => i.status === 'satisfactory') || [];
  const defectiveItems = dvir.inspection_items?.filter(i => i.status === 'defective') || [];
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden [&>button]:hidden">
        {/* Header - with safe area for iPhone notch */}
        <div className="flex-shrink-0 p-4 border-b bg-white" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ClipboardCheck className="w-5 h-5 text-blue-600" />
            DVIR Report
          </DialogTitle>
        </div>
        
        {/* Scrollable Report Content */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-2 sm:p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div ref={reportRef} className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden" style={{ pageBreakInside: 'avoid' }}>
            {/* Report Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold">Driver Vehicle Inspection Report</h1>
                  <p className="text-blue-100 mt-1 text-sm sm:text-base">Unit {dvir.unit_number}</p>
                </div>
                <div className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg self-start sm:self-auto ${dvir.has_defects ? 'bg-red-500' : 'bg-green-500'}`}>
                  {dvir.has_defects ? (
                    <span className="flex items-center gap-1.5 sm:gap-2 font-semibold text-sm sm:text-base">
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
                      {dvir.defect_count} Defect{dvir.defect_count !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 sm:gap-2 font-semibold text-sm sm:text-base">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                      All Clear
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Inspection Details */}
            <div className="p-4 sm:p-6 border-b" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">Inspection Details</h2>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="text-slate-500">Date:</span>
                  <div className="font-medium">{new Date(dvir.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                </div>
                <div>
                  <span className="text-slate-500">Time:</span>
                  <div className="font-medium">{new Date(dvir.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div>
                  <span className="text-slate-500">Inspector:</span>
                  <div className="font-medium">{dvir.inspector_name}</div>
                </div>
                <div>
                  <span className="text-slate-500">Odometer:</span>
                  <div className="font-medium">{dvir.mileage ? dvir.mileage.toLocaleString() + ' mi' : 'N/A'}</div>
                </div>
              </div>
            </div>
            
            {/* Vehicle Photos */}
            {(dvir.photo_front || dvir.photo_back || dvir.photo_left || dvir.photo_right) && (
              <div className="p-4 sm:p-6 border-b" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">Vehicle Photos</h2>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {dvir.photo_front && (
                    <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <p className="text-xs sm:text-sm text-slate-500 mb-1.5 sm:mb-2 font-medium">Front</p>
                      <img src={dvir.photo_front} alt="Front" className="w-full h-auto object-contain rounded-lg border bg-slate-50" style={{ maxHeight: '150px' }} />
                    </div>
                  )}
                  {dvir.photo_back && (
                    <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <p className="text-xs sm:text-sm text-slate-500 mb-1.5 sm:mb-2 font-medium">Back</p>
                      <img src={dvir.photo_back} alt="Back" className="w-full h-auto object-contain rounded-lg border bg-slate-50" style={{ maxHeight: '150px' }} />
                    </div>
                  )}
                  {dvir.photo_left && (
                    <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <p className="text-xs sm:text-sm text-slate-500 mb-1.5 sm:mb-2 font-medium">Left Side</p>
                      <img src={dvir.photo_left} alt="Left" className="w-full h-auto object-contain rounded-lg border bg-slate-50" style={{ maxHeight: '150px' }} />
                    </div>
                  )}
                  {dvir.photo_right && (
                    <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <p className="text-xs sm:text-sm text-slate-500 mb-1.5 sm:mb-2 font-medium">Right Side</p>
                      <img src={dvir.photo_right} alt="Right" className="w-full h-auto object-contain rounded-lg border bg-slate-50" style={{ maxHeight: '150px' }} />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Defects Section */}
            {defectiveItems.length > 0 && (
              <div className="p-4 sm:p-6 border-b bg-red-50" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <h2 className="text-base sm:text-lg font-semibold text-red-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Defects Found ({defectiveItems.length})
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  {defectiveItems.map((item, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 sm:p-4 border border-red-200" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <div className="font-semibold text-red-800 text-base sm:text-lg">{item.item_name}</div>
                      {item.remarks && (
                        <p className="text-xs sm:text-sm text-slate-700 mt-1.5 sm:mt-2">{item.remarks}</p>
                      )}
                      {item.photos && item.photos.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 sm:mt-4">
                          {item.photos.map((photo, pIdx) => (
                            <img 
                              key={pIdx} 
                              src={photo} 
                              alt={`Defect ${pIdx + 1}`} 
                              className="w-full h-auto object-contain rounded-lg border bg-slate-50" 
                              style={{ maxHeight: '140px', pageBreakInside: 'avoid', breakInside: 'avoid' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Satisfactory Items */}
            <div className="p-4 sm:p-6 border-b" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <h2 className="text-base sm:text-lg font-semibold text-green-800 mb-3 sm:mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Satisfactory Items ({satisfactoryItems.length})
              </h2>
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
                {satisfactoryItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-700">
                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                    {item.item_name}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Extra Remarks */}
            {dvir.extra_remarks && (
              <div className="p-4 sm:p-6 border-b" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">Additional Remarks</h2>
                <p className="text-xs sm:text-sm text-slate-700">{dvir.extra_remarks}</p>
              </div>
            )}
            
            {/* Footer */}
            <div className="p-4 sm:p-6 bg-slate-50 text-center text-xs sm:text-sm text-slate-500" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <p>Report generated: {new Date().toLocaleString()}</p>
              <p className="mt-1">Pacific Expressway Fleet Management</p>
            </div>
          </div>
        </div>
        
        {/* Footer Buttons - with safe area for iPhone home indicator */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-t bg-white flex flex-col gap-2 sm:gap-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <Button 
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="w-full h-11 sm:h-12 bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base"
            data-testid="btn-download-dvir-pdf"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Generating PDF..." : "Download PDF"}
          </Button>
          <Button variant="outline" onClick={onClose} className="w-full h-11 sm:h-12 text-sm sm:text-base">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main DVIR Section Component
export const DVIRSection = ({ vehicles = [], currentUser, autoOpenForm, onFormOpened, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  // Permission checks
  const userRole = currentUser?.role;
  const canCreate = hasPermission(userRole, PERMISSIONS.DVIR.CREATE);
  const canDelete = hasPermission(userRole, PERMISSIONS.DVIR.DELETE);
  const canViewReport = hasPermission(userRole, PERMISSIONS.DVIR.VIEW_REPORT);
  const canDownloadPDF = hasPermission(userRole, PERMISSIONS.DVIR.DOWNLOAD_PDF);
  
  // Form state
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mileage, setMileage] = useState("");
  const [photos, setPhotos] = useState({ front: null, back: null, left: null, right: null });
  const [inspectionItems, setInspectionItems] = useState({});
  const [extraRemarks, setExtraRemarks] = useState("");
  
  // Defect dialog state
  const [defectDialogOpen, setDefectDialogOpen] = useState(false);
  const [currentDefectItem, setCurrentDefectItem] = useState(null);
  
  // DVIR history state
  const [dvirsByDate, setDvirsByDate] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});
  const [loadingDvirs, setLoadingDvirs] = useState(true);
  
  // View/Delete state
  const [viewingDvir, setViewingDvir] = useState(null);
  const [deletingDvirId, setDeletingDvirId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Fetch DVIRs grouped by date
  const fetchDvirs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dvir/by-date`);
      setDvirsByDate(response.data);
    } catch (error) {
      console.error("Failed to fetch DVIRs", error);
    } finally {
      setLoadingDvirs(false);
    }
  }, []);
  
  useEffect(() => {
    fetchDvirs();
  }, [fetchDvirs]);
  
  // Handle auto-open form from navigation
  useEffect(() => {
    if (autoOpenForm) {
      // Small delay to ensure component is fully mounted and vehicles are ready
      const timer = setTimeout(() => {
        setShowForm(true);
        onFormOpened?.();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoOpenForm, onFormOpened]);
  
  const resetForm = () => {
    setStep(1);
    setSelectedVehicle(null);
    setMileage("");
    setPhotos({ front: null, back: null, left: null, right: null });
    setInspectionItems({});
    setExtraRemarks("");
    setShowForm(false);
  };
  
  const handleVehicleSelect = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setSelectedVehicle(vehicle);
    if (vehicle?.mileage) {
      setMileage(vehicle.mileage.toLocaleString());
    }
  };
  
  const handleMileageChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      setMileage(numValue > 0 ? numValue.toLocaleString() : '');
    }
  };
  
  const handleDefectDetails = (item) => {
    setCurrentDefectItem(item);
    setDefectDialogOpen(true);
  };
  
  const handleSaveDefect = (remarks, defectPhotos) => {
    if (currentDefectItem) {
      setInspectionItems(prev => ({
        ...prev,
        [currentDefectItem.key]: {
          status: 'defective',
          remarks,
          photos: defectPhotos
        }
      }));
    }
  };
  
  const handleClearDefect = (itemKey) => {
    setInspectionItems(prev => {
      const newItems = { ...prev };
      delete newItems[itemKey];
      return newItems;
    });
  };
  
  const handleSubmit = async () => {
    if (!selectedVehicle) {
      toast.error("Please select a vehicle");
      return;
    }
    
    // Check defective items have remarks
    const defectiveWithoutRemarks = INSPECTION_ITEMS.filter(
      item => inspectionItems[item.key]?.status === 'defective' && !inspectionItems[item.key]?.remarks
    );
    if (defectiveWithoutRemarks.length > 0) {
      toast.error("Please add remarks for all defective items");
      return;
    }
    
    setSubmitting(true);
    try {
      // All items default to satisfactory unless explicitly marked defective
      const payload = {
        vehicle_id: selectedVehicle.id,
        unit_number: selectedVehicle.unit_number,
        mileage: mileage ? parseInt(mileage.replace(/,/g, '')) : null,
        photo_front: photos.front,
        photo_back: photos.back,
        photo_left: photos.left,
        photo_right: photos.right,
        // All items default to satisfactory unless explicitly marked defective
        inspection_items: INSPECTION_ITEMS.map(item => ({
          item_key: item.key,
          item_name: item.name,
          status: inspectionItems[item.key]?.status || 'satisfactory',
          remarks: inspectionItems[item.key]?.remarks || null,
          photos: inspectionItems[item.key]?.photos || []
        })),
        extra_remarks: extraRemarks || null,
        inspector_name: `${currentUser.first_name} ${currentUser.last_name}`,
        inspector_id: currentUser.id
      };
      
      await axios.post(`${API}/dvir`, payload);
      toast.success("DVIR submitted successfully");
      resetForm();
      fetchDvirs();
      // Refresh vehicle data to show updated mileage
      onUpdate?.();
    } catch (error) {
      console.error("Failed to submit DVIR", error);
      toast.error("Failed to submit DVIR");
    } finally {
      setSubmitting(false);
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return selectedVehicle !== null;
      case 2: return true; // Photos are optional
      case 3: return true; // All items default to satisfactory, so always can proceed
      case 4: return true;
      default: return false;
    }
  };
  
  const getDefectCount = () => {
    return Object.values(inspectionItems).filter(item => item.status === 'defective').length;
  };
  
  const toggleDateExpanded = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };
  
  // View DVIR - Fetch full details
  const handleViewDvir = async (dvir) => {
    try {
      const response = await axios.get(`${API}/dvir/${dvir.id}`);
      setViewingDvir(response.data);
    } catch (error) {
      console.error("Failed to fetch DVIR details", error);
      toast.error("Failed to load report");
    }
  };
  
  // Delete DVIR
  const handleDeleteClick = (dvirId) => {
    setDeletingDvirId(dvirId);
    setDeleteConfirmOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!deletingDvirId) return;
    
    try {
      await axios.delete(`${API}/dvir/${deletingDvirId}`);
      toast.success("DVIR deleted successfully");
      setDeleteConfirmOpen(false);
      setDeletingDvirId(null);
      fetchDvirs(); // Refresh the list
    } catch (error) {
      console.error("Failed to delete DVIR", error);
      toast.error("Failed to delete DVIR");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">DVIR</h1>
          <p className="text-slate-500 mt-1">Driver Vehicle Inspection Reports</p>
        </div>
        {canCreate && (
          <Button 
            onClick={() => setShowForm(true)} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="btn-new-dvir"
          >
            <Plus className="w-4 h-4 mr-2" /> New Inspection
          </Button>
        )}
      </div>
      
      {/* Today's Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-blue-600">
            {dvirsByDate.find(d => d.date === new Date().toISOString().split('T')[0])?.count || 0}
          </div>
          <p className="text-xs text-slate-500">Today's Inspections</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-red-600">
            {dvirsByDate.find(d => d.date === new Date().toISOString().split('T')[0])?.has_defects_count || 0}
          </div>
          <p className="text-xs text-slate-500">With Defects</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-slate-700">
            {dvirsByDate.reduce((acc, d) => acc + d.count, 0)}
          </div>
          <p className="text-xs text-slate-500">Total DVIRs</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-slate-700">{dvirsByDate.length}</div>
          <p className="text-xs text-slate-500">Days Recorded</p>
        </div>
      </div>
      
      {/* DVIRs by Date */}
      <div className="space-y-3">
        {loadingDvirs ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : dvirsByDate.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <ClipboardCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No inspection reports yet</p>
            {canCreate && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" /> Start First Inspection
              </Button>
            )}
          </div>
        ) : (
          dvirsByDate.map((dateGroup) => (
            <div key={dateGroup.date} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => toggleDateExpanded(dateGroup.date)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold text-slate-900">{formatDate(dateGroup.date)}</div>
                    <div className="text-sm text-slate-500">
                      {dateGroup.count} inspection{dateGroup.count !== 1 ? 's' : ''}
                      {dateGroup.has_defects_count > 0 && (
                        <span className="text-red-600 ml-2">
                          • {dateGroup.has_defects_count} with defects
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedDates[dateGroup.date] ? 'rotate-180' : ''}`} />
              </button>
              
              {expandedDates[dateGroup.date] && (
                <div className="border-t border-slate-200 divide-y divide-slate-100">
                  {dateGroup.dvirs.map((dvir) => (
                    <div key={dvir.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            dvir.has_defects ? 'bg-red-100' : 'bg-green-100'
                          }`}>
                            {dvir.has_defects ? (
                              <AlertTriangle className="w-5 h-5 text-red-600" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">Unit {dvir.unit_number}</div>
                            <div className="text-sm text-slate-500">
                              {dvir.inspector_name} • {new Date(dvir.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                        {dvir.has_defects && (
                          <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full hidden sm:inline-block">
                            {dvir.defect_count} defect{dvir.defect_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center gap-2 mt-3 pl-13">
                        {canViewReport && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDvir(dvir)}
                            className="flex items-center gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`btn-view-dvir-${dvir.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            <span className="hidden xs:inline">View Report</span>
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(dvir.id)}
                            className="flex items-center gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`btn-delete-dvir-${dvir.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="hidden xs:inline">Delete</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      
      {/* New DVIR Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              New DVIR
            </DialogTitle>
          </DialogHeader>
          
          {/* Progress */}
          <div className="px-4 py-3 flex-shrink-0 bg-slate-50 border-b">
            <StepProgress currentStep={step} />
          </div>
          
          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-2xl mx-auto">
              {/* Step 1: Select Vehicle */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label>Select Vehicle *</Label>
                    <Select value={selectedVehicle?.id || ""} onValueChange={handleVehicleSelect}>
                      <SelectTrigger className="h-12 mt-1" data-testid="select-dvir-vehicle">
                        <SelectValue placeholder="Select a vehicle..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            Unit {v.unit_number} - {v.year} {v.make} {v.model}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedVehicle && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500">Type:</span> <span className="font-medium capitalize">{selectedVehicle.vehicle_type.replace('_', ' ')}</span></div>
                        <div><span className="text-slate-500">VIN:</span> <span className="font-medium">{selectedVehicle.vin}</span></div>
                        <div><span className="text-slate-500">License:</span> <span className="font-medium">{selectedVehicle.license_plate || 'N/A'}</span></div>
                        <div><span className="text-slate-500">Year:</span> <span className="font-medium">{selectedVehicle.year}</span></div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label>Current Odometer Reading</Label>
                    <Input
                      value={mileage}
                      onChange={handleMileageChange}
                      placeholder="e.g., 150,000"
                      className="h-12 mt-1"
                      inputMode="numeric"
                    />
                  </div>
                </div>
              )}
              
              {/* Step 2: Vehicle Photos */}
              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Take photos of all four sides of the vehicle. This helps document the vehicle's condition.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <PhotoUploadBox 
                      label="Front" 
                      photo={photos.front} 
                      onUpload={(p) => setPhotos(prev => ({ ...prev, front: p }))}
                      onRemove={() => setPhotos(prev => ({ ...prev, front: null }))}
                    />
                    <PhotoUploadBox 
                      label="Back" 
                      photo={photos.back} 
                      onUpload={(p) => setPhotos(prev => ({ ...prev, back: p }))}
                      onRemove={() => setPhotos(prev => ({ ...prev, back: null }))}
                    />
                    <PhotoUploadBox 
                      label="Left Side" 
                      photo={photos.left} 
                      onUpload={(p) => setPhotos(prev => ({ ...prev, left: p }))}
                      onRemove={() => setPhotos(prev => ({ ...prev, left: null }))}
                    />
                    <PhotoUploadBox 
                      label="Right Side" 
                      photo={photos.right} 
                      onUpload={(p) => setPhotos(prev => ({ ...prev, right: p }))}
                      onRemove={() => setPhotos(prev => ({ ...prev, right: null }))}
                    />
                  </div>
                </div>
              )}
              
              {/* Step 3: Inspection Items */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800 font-medium">
                      All items are OK by default. Tap only on items with defects.
                    </p>
                    {getDefectCount() > 0 && (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        {getDefectCount()} defect{getDefectCount() !== 1 ? 's' : ''} marked
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {INSPECTION_ITEMS.map((item) => (
                      <InspectionItemRow
                        key={item.key}
                        item={item}
                        status={inspectionItems[item.key]?.status || 'satisfactory'}
                        remarks={inspectionItems[item.key]?.remarks}
                        photos={inspectionItems[item.key]?.photos}
                        onMarkDefective={handleDefectDetails}
                        onEditDefect={handleDefectDetails}
                        onClearDefect={handleClearDefect}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Step 4: Review & Submit */}
              {step === 4 && (
                <div className="space-y-6">
                  {/* Summary Card */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <h3 className="font-semibold text-slate-900 mb-3">Inspection Summary</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Vehicle:</span>
                        <div className="font-medium">Unit {selectedVehicle?.unit_number}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Odometer:</span>
                        <div className="font-medium">{mileage || 'Not recorded'}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Inspector:</span>
                        <div className="font-medium">{currentUser?.first_name} {currentUser?.last_name}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Date:</span>
                        <div className="font-medium">{new Date().toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Results */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-green-700">
                        {INSPECTION_ITEMS.length - getDefectCount()}
                      </div>
                      <p className="text-sm text-green-600">Satisfactory</p>
                    </div>
                    <div className={`rounded-xl p-4 border text-center ${
                      getDefectCount() > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <XCircle className={`w-8 h-8 mx-auto mb-2 ${getDefectCount() > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                      <div className={`text-2xl font-bold ${getDefectCount() > 0 ? 'text-red-700' : 'text-slate-400'}`}>
                        {getDefectCount()}
                      </div>
                      <p className={`text-sm ${getDefectCount() > 0 ? 'text-red-600' : 'text-slate-500'}`}>Defective</p>
                    </div>
                  </div>
                  
                  {/* Defects List */}
                  {getDefectCount() > 0 && (
                    <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                      <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Defects Found
                      </h4>
                      <ul className="space-y-2">
                        {INSPECTION_ITEMS.filter(item => inspectionItems[item.key]?.status === 'defective').map(item => (
                          <li key={item.key} className="text-sm text-red-700">
                            <span className="font-medium">{item.name}:</span> {inspectionItems[item.key]?.remarks}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Extra Remarks */}
                  <div>
                    <Label>Additional Remarks (Optional)</Label>
                    <Textarea
                      value={extraRemarks}
                      onChange={(e) => setExtraRemarks(e.target.value)}
                      placeholder="Any additional comments about the inspection..."
                      className="min-h-[100px] mt-1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <div className="flex gap-3 max-w-2xl mx-auto">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                  Back
                </Button>
              )}
              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="btn-submit-dvir"
                >
                  {submitting ? "Submitting..." : "Submit DVIR"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Defect Details Dialog */}
      <DefectDetailsDialog
        open={defectDialogOpen}
        onClose={() => setDefectDialogOpen(false)}
        item={currentDefectItem}
        remarks={currentDefectItem ? inspectionItems[currentDefectItem.key]?.remarks : ''}
        photos={currentDefectItem ? inspectionItems[currentDefectItem.key]?.photos : []}
        onSave={handleSaveDefect}
      />
      
      {/* DVIR Report Viewer Modal */}
      <DVIRReportViewer
        open={!!viewingDvir}
        onClose={() => setViewingDvir(null)}
        dvir={viewingDvir}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete DVIR?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete this inspection report? This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeletingDvirId(null);
              }} 
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700"
              data-testid="btn-confirm-delete-dvir"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
